// File: frontend/src/pages/club/ClubPage.tsx
// src/pages/club/ClubPage.tsx

import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RiZoomInLine, RiCloseLine } from "react-icons/ri";
import "./ClubPage.css";
import { ClubModel, ClubTeamSummary } from "../../models/ClubModel";
import { clubService } from "../../services/clubService";
import { postClubEntry, fetchEntriesByClubId } from "../../services/clubEntryService";
import { RedirectError } from "../../services/apiClient";
import { resolveClubName } from "../../services/matchService";
import { getCountryNameTr } from "../../utils/countryUtils";
import { useClubEntries } from "../../hooks/useClubEntries";
import { useCollapsedOldEntries, CollapseEntryFetcher } from "../../hooks/useCollapsedOldEntries";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import { useUrlPage, pageSuffixes, useIsEntryDeepLink, useFocusEntryId, useExitFocus } from "../../hooks/useUrlPage";
import { useFocusedEntry } from "../../hooks/useFocusedEntry";
import { Pagination } from "../../utils";
import { absoluteUrl, clubUrl, withCurrentQueryAndHash } from "../../utils/canonicalUrl";
import { buildTopicShareUrl } from "../../utils/shareUtils";
import { SpinnerLoading } from "../../utils";
import { EntryRow } from "../../components/entries/EntryRow";
import { CollapsedEntriesBar } from "../../components/entries/CollapsedEntriesBar";
// Sitewide pins: admin-pinned entry shown at the top of the topic page
import { PinnedEntryBlock } from "../../components/entries/PinnedEntryBlock";
import { EntryToolbar, EntrySortOption } from "../../components/entries/EntryToolbar";
import { EntryPostForm } from "../../components/entries/EntryPostForm";
import { usePreferences } from "../../context/PreferencesContext";
import { useAuth } from "../../context/AuthContext";
import { ENTRIES_PER_PAGE } from "../../constants";
import {
  FaShieldAlt, FaArrowLeft,
  FaFutbol, FaBasketballBall, FaVolleyballBall,
  FaFootballBall, FaBaseballBall, FaHockeyPuck,
} from "react-icons/fa";
import { MdSportsHandball } from "react-icons/md";

interface ClubPageProps {
  clubSlug: string;
  // Called after a new entry is successfully posted so App.tsx can
  // refresh the club list and re-sort it (e.g. move this club to top).
  onEntryPosted?: () => void;
  // Called when the mobile back button is tapped to return to the list pane
  onMobileBack?: () => void;
}

/**
 * Builds an optional " {Ülke}, {yıl} yılında kurulmuş bir kulüp." suffix for
 * the meta description. Returns an empty string when country/foundedYear are
 * missing so the caller can append this directly onto the base sentence.
 */
const buildClubFoundedSuffix = (club: ClubModel | null): string => {
  if (!club) return "";
  const country = getCountryNameTr(club.country);
  if (country && club.foundedYear) {
    return ` ${country}, ${club.foundedYear} yılında kurulmuş bir kulüp.`;
  }
  if (country) {
    return ` ${country} temelli bir kulüp.`;
  }
  return "";
};

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";

const resolveUrl = (url: string | null): string | null => {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API_BASE}${url}`;
};

// Turkish URL slug for each sport type — mirrors the mapping in TeamModel.ts
const SPORT_SLUG_MAP: Record<string, string> = {
  FOOTBALL:          "futbol",
  BASKETBALL:        "basketbol",
  VOLLEYBALL:        "voleybol",
  AMERICAN_FOOTBALL: "amerikan-futbol",
  BASEBALL:          "beyzbol",
  ICE_HOCKEY:        "buz-hokeyi",
  HANDBALL:          "hentbol",
};

const SPORT_LABELS: Record<string, string> = {
  FOOTBALL:          "Futbol",
  BASKETBALL:        "Basketbol",
  VOLLEYBALL:        "Voleybol",
  AMERICAN_FOOTBALL: "Amerikan Futbolu",
  BASEBALL:          "Beyzbol",
  ICE_HOCKEY:        "Buz Hokeyi",
  HANDBALL:          "Hentbol",
};

const buildTeamPath = (clubSlug: string, team: ClubTeamSummary): string => {
  const sport      = SPORT_SLUG_MAP[team.sportType] ?? team.sportType.toLowerCase();
  const gender     = team.gender === "FEMALE" ? "-kadin" : team.gender === "MIXED" ? "-karma" : "";
  const codeSuffix = team.teamCode ? `-${team.teamCode}` : "";
  return `/takim/${clubSlug}/${sport}${gender}${codeSuffix}`;
};

const SportIcon: React.FC<{ sportType: string; size?: number }> = ({ sportType, size = 13 }) => {
  const p = { size };
  switch (sportType) {
    case "FOOTBALL":          return <FaFutbol {...p} />;
    case "BASKETBALL":        return <FaBasketballBall {...p} />;
    case "VOLLEYBALL":        return <FaVolleyballBall {...p} />;
    case "AMERICAN_FOOTBALL": return <FaFootballBall {...p} />;
    case "BASEBALL":          return <FaBaseballBall {...p} />;
    case "ICE_HOCKEY":        return <FaHockeyPuck {...p} />;
    case "HANDBALL":          return <MdSportsHandball {...p} />;
    default:                  return <FaFutbol {...p} />;
  }
};

const teamBranchLabel = (team: ClubTeamSummary): string => {
  const sport      = SPORT_LABELS[team.sportType] ?? team.sportType;
  const gender     = team.gender === "FEMALE" ? " (K)" : team.gender === "MIXED" ? " (M)" : "";
  const codeSuffix = team.teamCode ? ` ${team.teamCode.toUpperCase()}` : "";
  return `${sport}${gender}${codeSuffix}`;
};

/**
 * Builds a CSS gradient string from up to three club brand colors.
 * When no colors are defined, falls back to a dark neutral grey.
 * Club colors are used as-is so actual brand identity is preserved.
 * White text readability is ensured via strong text-shadow on the text elements.
 */
const buildBannerGradient = (c1: string | null, c2: string | null, c3: string | null): string => {
  const colors = [c1, c2, c3].filter(Boolean) as string[];
  if (colors.length === 0) return "linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%)";
  if (colors.length === 1) return `linear-gradient(135deg, ${colors[0]} 0%, ${colors[0]}99 100%)`;
  if (colors.length === 2) return `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`;
  return `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 50%, ${colors[2]} 100%)`;
};

export const ClubPage: React.FC<ClubPageProps> = ({ clubSlug, onEntryPosted, onMobileBack }) => {
  const { t } = useTranslation();
  const { preferences } = usePreferences();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [club, setClub]       = useState<ClubModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // Club logo: popover open state, and whether the logo URL 404s/fails to
  // load (stale or wrong link) — in that case we fall back to the shield
  // icon instead of a broken-image glyph.
  // Mirrors the person page avatar popover (PersonPage.tsx) one-to-one.
  const [logoOpen, setLogoOpen]     = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  // Screen coordinates the popover renders at — computed from the logo's own
  // position when opened, so it appears right next to the logo it enlarges
  // instead of dead-center of the screen. Rendered via a portal (see below),
  // so this is relative to the viewport, not this component.
  const logoBtnRef = useRef<HTMLButtonElement>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);

  // Mirrors the CSS sizing on .club-page__logo-popover img — kept in sync
  // manually since the clamp math below needs the same numbers CSS uses.
  // Club logos are square 150x150 sources, so the box is square too.
  const POPOVER_W = 150;
  const POPOVER_H = 150;
  const POPOVER_MARGIN = 16;

  const openLogo = () => {
    // Mobile: always center — anchoring next to a tiny header logo on a
    // narrow screen has nowhere good to go and just looks broken. Desktop
    // keeps the "pop out right next to the logo" behavior.
    const isMobile = window.innerWidth <= 768;
    const rect = isMobile ? undefined : logoBtnRef.current?.getBoundingClientRect();
    const effW = Math.min(window.innerWidth * 0.55, POPOVER_W);
    const effH = Math.min(window.innerHeight * 0.45, POPOVER_H);
    if (rect) {
      let left = rect.left;
      let top = rect.bottom + 10;
      // Keep it on-screen: slide left if it would overflow the right edge...
      if (left + effW + POPOVER_MARGIN > window.innerWidth) {
        left = Math.max(POPOVER_MARGIN, window.innerWidth - effW - POPOVER_MARGIN);
      }
      // ...and flip above the logo if there isn't enough room below it.
      if (top + effH + POPOVER_MARGIN > window.innerHeight) {
        top = Math.max(POPOVER_MARGIN, rect.top - effH - 10);
      }
      setPopoverPos({ top, left });
    } else {
      setPopoverPos(null);
    }
    setLogoOpen(true);
  };

  // ESC closes the logo popover, same convention as other overlays.
  useEffect(() => {
    if (!logoOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLogoOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [logoOpen]);

  // A different club was opened in the left pane while the popover was up —
  // close it and drop the stale failure flag so the new logo gets a fresh try.
  useEffect(() => {
    setLogoOpen(false);
    setLogoFailed(false);
  }, [clubSlug]);

  // Sets document.title / meta description / canonical / OG tags for this
  // club's page once it has loaded. Falls back to the site-wide defaults
  // (see useDocumentMeta) while loading or on load failure.
  const clubDisplayName = club ? resolveClubName(club) : null;
  // Entry page number lives in the URL ("?sayfa=2") rather than in state, so a
  // page can be linked to and shared, survives a refresh, and gives each page
  // its own indexable address.
  const { page: urlPage, setPage: setUrlPage } = useUrlPage();

  useDocumentMeta({
    title: clubDisplayName ? `${clubDisplayName}${pageSuffixes(urlPage).titleSuffix} - Üç Korner Bir Penaltı` : undefined,
    description: clubDisplayName
      ? `${clubDisplayName} hakkında yazılan tanımları oku, sen de yorumunu ekle, tartış ve oyla.${buildClubFoundedSuffix(club)}`
      : undefined,
    canonicalUrl: club ? absoluteUrl(clubUrl(club.slug, club.id)) + pageSuffixes(urlPage).urlSuffix : undefined,
    imageUrl: club?.logoUrl || undefined,
  });

  // Toolbar state: keyword search, filters, and sort for the entry list
  const [sort, setSort]               = useState<EntrySortOption>("oldest");
  const [filterToday, setFilterToday] = useState(false);
  const [rookieOnly, setRookieOnly]   = useState(false);
  const [keyword, setKeyword]         = useState("");

  // Anchor used by the toolbar "sayfa sonu" button to scroll down to the entry form
  const bottomRef = useRef<HTMLDivElement>(null);
  const handleScrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const showRookieEntries = preferences?.showRookieEntries !== false;
  const entriesPerPage    = preferences?.entriesPerPage ?? ENTRIES_PER_PAGE;
  const clubId            = club?.id ?? null;

  // Sort oldest-first so the newest entry lands at the bottom, just above the
  // input form — matching the PersonPage and match-page layout.
  const {
    entries, loading: loadingEntries, error: entriesError,
    totalItems, currentPage, totalPages,
    isLoadingMore, loadEntries, loadMoreEntries, updateEntries,
  } = useClubEntries(clubId, showRookieEntries, sort, entriesPerPage, filterToday, rookieOnly, keyword, urlPage);

  // Only active in the default view: oldest sort, no filters, no keyword.
  // An address that names a page ("?sayfa=4") or an entry ("#entry-1499") is a
  // deep link, and the collapsed view cannot serve it: it renders only today's
  // entries and hides the pagination controls, so the requested page was
  // fetched and never shown and the anchored entry was never in the DOM for
  // the scroll to find. An explicit request wins over the default view.
  const isDeepLink = useIsEntryDeepLink();
  const collapseEligible =
    sort === "oldest" && !filterToday && !rookieOnly && !keyword.trim() && !isDeepLink;

  // Fetcher used by the collapse hook — always oldest sort, viewer's rookie preference
  const collapseFetcher = useCallback<CollapseEntryFetcher>(async (page, size, fToday) => {
    if (clubId === null) throw new Error("No club selected");
    const res = await fetchEntriesByClubId(clubId, page, size, showRookieEntries, "oldest", fToday, false, "");
    return { entries: res.entries, totalItems: res.totalItems, totalPages: res.totalPages };
  }, [clubId, showRookieEntries]);

  const collapse = useCollapsedOldEntries(clubId, collapseEligible, entriesPerPage, collapseFetcher);

  // Permalink view: "?tanim=747" shows that one tanım framed by "{n} entry
  // daha" bars instead of dropping the reader into the middle of a page. It
  // reuses the collapse fetcher, so nothing new has to be wired per domain.
  const focusEntryId = useFocusEntryId();
  const exitFocus = useExitFocus();
  const focus = useFocusedEntry(clubId, focusEntryId, entriesPerPage, collapseFetcher);
  const handleExitFocus = useCallback(() => {
    if (focus.entry) exitFocus(focus.page, focus.entry.id);
  }, [exitFocus, focus.entry, focus.page]);

  // Entries actually rendered: collapsed view (today's + revealed older) or the normal list
  // Focused permalink view wins over both the collapsed view and the normal
  // list: the reader asked for one specific tanım.
  const displayEntries = focus.active
    ? (focus.entry ? [focus.entry] : [])
    : (collapse.active ? collapse.entries : entries);

  // Apply local mutations (vote, delete, new post) to both lists so the visible one stays correct
  const applyEntriesUpdate = useCallback((fn: (prev: typeof entries) => typeof entries) => {
    updateEntries(fn);
    collapse.updateEntries(fn);
  }, [updateEntries, collapse.updateEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Ticket 1c: the header (name, logo, colors, share link) comes from this
    // request while the entry list comes from a second, independent one. Two
    // quick clicks in the left pane leave two club requests in flight, and
    // without this flag the slower one wins simply by arriving last — which is
    // how the page ended up showing one club's header above another club's
    // entries. React runs the cleanup before re-running the effect, so the
    // superseded request is marked stale the instant a new slug is requested.
    let cancelled = false;

    setLoading(true);
    setError(null);
    setClub(null);
    clubService.getClubBySlug(clubSlug)
      .then((loaded) => {
        if (cancelled) return;
        setClub(loaded);
      })
      .catch((err) => {
        if (cancelled) return;
        // The club was merged — navigate to the surviving club page
        // Covers both a merged-away topic (410) and a non-canonical
        // slug (301) — both mean "continue at redirectPath".
        if (err instanceof RedirectError) {
          // Keep "?sayfa=" and "#entry-": this fires for every legacy or
          // non-canonical slug, which is exactly what an older shared deep
          // link is. Passing the path alone dropped the page and the anchor,
          // so the reader landed on page 1 with nothing to scroll to.
          navigate(withCurrentQueryAndHash(err.redirectPath), { replace: true });
          return;
        }
        setError(t("club_load_error", "Kulüp bilgileri yüklenemedi."));
      })
      .finally(() => {
        // Ticket 1c: only the request that is still current may clear the
        // spinner — otherwise the newer club renders with stale header state.
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [clubSlug, t, navigate]);

  const handlePostEntry = useCallback(async (content: string) => {
    if (!clubId) return;
    const newEntry = await postClubEntry(content, clubId);
    // Append to end so the new entry appears just above the input form
    applyEntriesUpdate(prev => [...prev, newEntry]);
    loadEntries(1);
    // Notify App.tsx so it can refresh the club list and re-sort it
    onEntryPosted?.();
  }, [clubId, applyEntriesUpdate, loadEntries, onEntryPosted]);

  const handleDeleteEntry = (entryId: number) => {
    applyEntriesUpdate(prev => prev.filter(e => e.id !== entryId));
  };

  if (loading) return <SpinnerLoading />;
  if (error)   return <div className="club-page__error">{error}</div>;
  if (!club)   return <div className="club-page__not-found">{t("club_not_found", "Kulüp bulunamadı.")}</div>;

  const logoSrc     = resolveUrl(club.logoUrl);
  // Only offer the enlarge interaction while the logo actually renders
  const hasLogo     = Boolean(logoSrc) && !logoFailed;
  const isFlagLogo  = club.logoUrl?.includes("flags/") ?? false;
  const bannerStyle = { background: buildBannerGradient(club.color1, club.color2, club.color3) };

  // Build the bottom-right meta string: full country name + founded year
  const countryName = getCountryNameTr(club.country);
  const metaParts   = [countryName, club.foundedYear ? String(club.foundedYear) : null].filter(Boolean);

  return (
    <div className="club-page">
      {/* Mobile back button — hidden on desktop via CSS, visible on mobile */}
      {onMobileBack && (
        <button className="mobile-back-btn" onClick={onMobileBack} aria-label="Geri">
          <FaArrowLeft size={14} />
          {t("mobile_back", "Geri")}
        </button>
      )}

      {/* Full-width colored banner — makes club pages instantly distinct from team pages.
          Top row: logo (left) | club name centred (flex:1) | KULÜP pill (right).
          Bottom row: long name (left) | country · year (right).
          Teams strip: sport branch buttons flush to the bottom edge. */}
      <div className="club-page__banner" style={bannerStyle}>

        {/* Top row */}
        <div className="club-page__banner-top">
          <div className="club-page__banner-logo">
            {hasLogo ? (
              <button
                ref={logoBtnRef}
                type="button"
                className="club-page__banner-logo-btn"
                onClick={openLogo}
                title={t("club_view_logo", "Logoyu büyüt")}
                aria-label={t("club_view_logo", "Logoyu büyüt")}
              >
                <img
                  src={logoSrc as string}
                  alt={club.shortName}
                  className={isFlagLogo ? "flag-img" : "logo-img"}
                  onError={() => setLogoFailed(true)}
                />
                {/* Hover/focus affordance: dim overlay + magnifier, same
                    convention as the person page avatar. */}
                <span className="club-page__banner-logo-overlay" aria-hidden="true">
                  <RiZoomInLine />
                </span>
              </button>
            ) : (
              <FaShieldAlt size={44} className="club-page__banner-icon" />
            )}
          </div>
          <h1 className="club-page__name">{resolveClubName(club)}</h1>
          <span className="club-page__type-label">{t("club_type_badge", "KULÜP")}</span>
        </div>

        {/* Bottom info row: long name left, country · year right */}
        <div className="club-page__banner-bottom">
          {club.longName && <span className="club-page__long-name">{club.longName}</span>}
          {metaParts.length > 0 && (
            <span className="club-page__meta-right">{metaParts.join(" · ")}</span>
          )}
        </div>

        {/* Teams strip — sport branch buttons inside the banner */}
        {club.teams && club.teams.length > 0 && (
          <div className="club-page__teams-strip">
            <span className="club-page__teams-label">{t("club_teams_section", "Takımlar")}:</span>
            {club.teams.map(team => (
              <button
                key={team.teamId}
                className="club-page__team-btn"
                onClick={() => navigate(buildTeamPath(club.slug, team))}
              >
                <SportIcon sportType={team.sportType} size={12} />
                <span>{teamBranchLabel(team)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {logoOpen && hasLogo && ReactDOM.createPortal(
        // Deliberately NOT the full-screen ImageLightbox used for entry
        // uploads — a club crest only needs a modest zoom-in next to the
        // logo it belongs to, not a full takeover of the screen.
        //
        // Portalled to document.body (same pattern as ImageLightbox /
        // ConfirmModal): rendering this inline would make `position: fixed`
        // resolve against whatever ancestor happens to have a `transform`
        // on it (route-transition wrappers, etc.) instead of the real
        // viewport, which is what would push it into a random corner.
        <div
          className="club-page__logo-popover-backdrop"
          onClick={() => setLogoOpen(false)}
        >
          <div
            className="club-page__logo-popover"
            // Falls back to the CSS default (viewport-centered) if we
            // somehow don't have a measured position yet.
            style={
              popoverPos
                ? { top: popoverPos.top, left: popoverPos.left, transform: "none" }
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="club-page__logo-popover-close"
              onClick={() => setLogoOpen(false)}
              aria-label={t("close", "Kapat")}
            >
              <RiCloseLine />
            </button>
            <img src={logoSrc as string} alt={club.shortName} />
          </div>
        </div>,
        document.body
      )}

      {/* Toolbar: search, filters, sort, scroll-to-bottom — same behaviour as match pages */}
      <EntryToolbar
        sort={sort}
        onSortChange={setSort}
        filterToday={filterToday}
        onFilterTodayChange={setFilterToday}
        rookieOnly={rookieOnly}
        onRookieOnlyChange={setRookieOnly}
        keyword={keyword}
        onKeywordChange={setKeyword}
        onScrollToBottom={handleScrollToBottom}
        shareUrl={club ? buildTopicShareUrl(clubUrl(club.slug, club.id)) : undefined}
        shareTitle={resolveClubName(club)}
      />

      {/* Entries — oldest first so the newest lands just above the input form */}
      <div className="club-page__entries">
        {focus.loading || collapse.checking || (loadingEntries && !collapse.active && !focus.active) ? (
          <SpinnerLoading />
        ) : entriesError && !collapse.active ? (
          <p className="club-page__empty" style={{ color: "var(--color-error)" }}>{entriesError}</p>
        ) : displayEntries.length === 0 ? (
          <p className="club-page__empty">{t("club_no_entries", "Henüz tanım yok.")}</p>
        ) : (
          <>
            {/* Sözlük-convention bar: older entries are collapsed, clicking reveals the previous chunk */}
            {/* Permalink view: the shared tanım is framed by a "{n} entry daha"
                bar on each side. Clicking either one leaves the focused view
                for the full page this entry sits on, with the entry still on
                screen — the bars are the way back into the topic. */}
            {focus.active && (
              <CollapsedEntriesBar
                hiddenCount={focus.hiddenAbove}
                isLoading={false}
                onClick={handleExitFocus}
              />
            )}
            {collapse.active && (
              <CollapsedEntriesBar
                hiddenCount={collapse.hiddenCount}
                isLoading={collapse.isLoadingOlder}
                onClick={collapse.loadOlderEntries}
              />
            )}
            {/* Admin-pinned entry of this topic — rendered above the regular list */}
            <PinnedEntryBlock type="CLUB" targetId={clubId} />
            {displayEntries.map(entry => (
              <EntryRow
                key={entry.id}
                entry={entry}
                onDelete={handleDeleteEntry}
                onVoteUpdate={(entryId, updates) =>
                  applyEntriesUpdate(prev => prev.map(e => e.id === entryId ? { ...e, ...updates } : e))
                }
              />
            ))}
            {focus.active && (
              <CollapsedEntriesBar
                hiddenCount={focus.hiddenBelow}
                isLoading={false}
                onClick={handleExitFocus}
              />
            )}
            {/* Page navigation. Reads and writes the "?sayfa=" URL parameter, so the
                reader can link to a page and the back button returns to it. */}
            {!focus.active && totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                paginate={setUrlPage}
              />
            )}
            {!focus.active && !collapse.active && currentPage < totalPages && (
              <button className="club-page__load-more" onClick={loadMoreEntries} disabled={isLoadingMore}>
                {isLoadingMore
                  ? t("load_more_loading", "yükleniyor...")
                  : t("load_more_entries", "{{count}} tanım daha", { count: totalItems - entries.length })}
              </button>
            )}
          </>
        )}
      </div>

      {/* Entry post form — always at the bottom, below all entries */}
      {/* Anchor for the toolbar "sayfa sonu" scroll target */}
      <div ref={bottomRef} />

      {isAuthenticated ? (
        <EntryPostForm
          selectedMatch={null}
          clubId={clubId}
          teamId={null}
          onPostEntry={handlePostEntry}
          isLocked={club?.isLocked ?? false}
          lockReason={club?.lockReason}
        />
      ) : (
        <p className="login-prompt-message">{t("login_to_post_entry")}</p>
      )}
    </div>
  );
};